import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { ClockNewIcon, MoonIcon } from '@jsmdg/yoshi';
import { type ActivityDuration, type ActivityDurationRange } from '../../../shared/types';
import styles from './ProductDuration.module.scss';

type ProductDurationProps = {
    readonly activityDurationRange?: ActivityDurationRange;
    readonly numberOfTravelNights?: number;
    readonly className?: string;
};

const messages = defineMessages({
    nightsLabel: {
        defaultMessage: '{nights} {nights, plural, one {Nacht} other {Nächte}}',
    },
    hoursLabel: {
        defaultMessage: '{hours} {hours, plural, one {Stunde} other {Stunden}}',
    },
    minutesLabel: {
        defaultMessage: '{minutes} {minutes, plural, one {Minute} other {Minuten}}',
    },
    maxDurationLabel: {
        defaultMessage: 'max. {duration}',
    },
});

const toTotalMinutes = ({ days = 0, hours = 0, minutes = 0 }: ActivityDuration): number =>
    (days * 24 + hours) * 60 + minutes;

// 1 hour 20 minutes becomes 1.3, 2 hours 30 minutes becomes 2.5
const toDecimalHours = (totalMinutes: number): number => Math.round((totalMinutes / 60) * 10) / 10;

const ProductDuration = ({
    activityDurationRange,
    className,
    numberOfTravelNights,
}: ProductDurationProps): JSX.Element | null => {
    const intl = useIntl();

    if (numberOfTravelNights && numberOfTravelNights > 0) {
        return (
            <div className={classNames('d-flex align-items-baseline', className)}>
                <MoonIcon ariaHidden className={styles.icon} />
                <span>
                    {intl.formatMessage(messages.nightsLabel, { nights: numberOfTravelNights })}
                </span>
            </div>
        );
    }

    if (!activityDurationRange) {
        return null;
    }

    const maxTotalMinutes = toTotalMinutes(activityDurationRange.max);

    if (!maxTotalMinutes) {
        return null;
    }

    const hours = Math.floor(maxTotalMinutes / 60);
    const minutes = maxTotalMinutes % 60;
    const duration =
        hours > 0 && minutes > 0
            ? intl.formatMessage(messages.hoursLabel, {
                  hours: intl.formatNumber(toDecimalHours(maxTotalMinutes), {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                  }),
              })
            : [
                  hours ? intl.formatMessage(messages.hoursLabel, { hours }) : null,
                  minutes ? intl.formatMessage(messages.minutesLabel, { minutes }) : null,
              ]
                  .filter(Boolean)
                  .join(' ');

    const hasDurationRange = toTotalMinutes(activityDurationRange.min) !== maxTotalMinutes;

    return (
        <div className={classNames('d-flex align-items-baseline', className)}>
            <ClockNewIcon ariaHidden className={styles.icon} />
            <span>
                {hasDurationRange
                    ? intl.formatMessage(messages.maxDurationLabel, { duration })
                    : duration}
            </span>
        </div>
    );
};

export { ProductDuration };
